import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Portal from '@mui/material/Portal'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import StateImage from 'components/state-image'
import useLocales from 'hooks/useLocales'
import { CAPTCHA_FAILURE_CODES } from 'helpers/captcha/contracts'
import type {
  CaptchaChallenge,
  CaptchaSolveResult,
  CaptchaSolver,
  CaptchaWidgetErrorReporter,
} from 'helpers/captcha/contracts'

const CAPTCHA_SILENT_TIMEOUT_MS = 20_000
const CAPTCHA_DIALOG_MAX_WIDTH = 480
const CAPTCHA_BACKDROP_COLOR = 'rgba(0, 0, 0, 0.54)'
const CAPTCHA_CONTAINER_MIN_HEIGHT = 65
// Turnstile's floor for `size: 'flexible'`; below it the widget overflows.
const CAPTCHA_WIDGET_MIN_WIDTH = 300
const CAPTCHA_TIMEOUT_ERROR_CODE = 'timeout'
const CAPTCHA_WATCHDOG_ERROR_CODE = 'watchdog_timeout'
const CAPTCHA_SCRIPT_ERROR_CODE = 'script_load_failed'

interface TurnstileRenderOptions {
  sitekey: string
  language: string
  theme: 'light'
  appearance: 'interaction-only'
  retry: 'never'
  'refresh-expired': 'manual'
  'refresh-timeout': 'manual'
  'response-field': false
  size: 'flexible'
  action?: string
  cData?: string
  callback: (token: unknown) => void
  'expired-callback': () => void
  'timeout-callback': () => void
  'error-callback': (errorCode?: string | number) => boolean
  'unsupported-callback': () => void
  'before-interactive-callback': () => void
  'after-interactive-callback': () => void
}

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions,
  ) => string
  remove: (widgetId: string) => void
}

interface WidgetInstance {
  api: TurnstileApi
  generation: number
  id: string
  removed: boolean
}

interface ChallengeView {
  challenge: CaptchaChallenge
  generation: number
  interactionVisible: boolean
  showError: boolean
}

export type CaptchaScriptStatus = 'loading' | 'loaded' | 'failed'

interface PendingSolve {
  challenge: CaptchaChallenge
  action?: string
  cData?: string
  generation: number | undefined
  onWidgetError: CaptchaWidgetErrorReporter
  resolve: (result: CaptchaSolveResult) => void
  settled: boolean
  stale: boolean
  showError: boolean
  waitingForScriptLoad: boolean
  scriptStatusAtRetry?: CaptchaScriptStatus
  watchdog?: number
  widget?: WidgetInstance
  callbackToken?: symbol
}

interface CaptchaChallengeContextValue {
  solveCaptcha: CaptchaSolver
  cancelCaptcha: () => void
}

interface CaptchaChallengeProviderProps {
  children: ReactNode
  scriptStatus?: CaptchaScriptStatus
  onRequestScript?: () => void
  onRetryScript?: () => void
}

const CaptchaChallengeContext =
  createContext<CaptchaChallengeContextValue | null>(null)

function getTurnstile(): TurnstileApi | undefined {
  if (typeof window === 'undefined') return undefined
  return (window as Window & { turnstile?: TurnstileApi }).turnstile
}

function isCurrentGeneration(
  pending: PendingSolve,
  generation: number,
  active: PendingSolve | null,
): boolean {
  return (
    active === pending &&
    !pending.settled &&
    !pending.stale &&
    !pending.showError &&
    pending.generation === generation &&
    (typeof document === 'undefined' ||
      document.visibilityState !== 'hidden')
  )
}

function stopWatchdog(pending: PendingSolve): void {
  if (pending.watchdog) {
    window.clearTimeout(pending.watchdog)
    pending.watchdog = undefined
  }
}

function removeCurrentWidget(
  pending: PendingSolve,
  generation?: number,
): void {
  const widget = pending.widget
  if (
    !widget ||
    (generation !== undefined && widget.generation !== generation)
  ) {
    return
  }

  pending.widget = undefined
  pending.callbackToken = undefined
  if (widget.removed) return
  widget.removed = true
  try {
    widget.api.remove(widget.id)
  } catch {
    // The solve must still settle if Turnstile cannot remove a failed widget.
  }
}

function blurActiveElement(): void {
  if (typeof document === 'undefined') return
  const activeElement = document.activeElement
  if (activeElement instanceof HTMLElement) activeElement.blur()
}

/**
 * Presents the Turnstile challenge for `solveCaptcha`. The widget renders in
 * an invisible overlay and dialog chrome only appears once Turnstile asks for
 * interaction or the challenge fails; a silent solve that stalls ends in the
 * failure state after a timeout.
 *
 * Every solve settles exactly once, as cancelled on close, backdrop, Escape,
 * route change, a newer solve or unmount. The page owns loading the Turnstile
 * script: `onRequestScript` fires on each solve and `onRetryScript` reloads
 * it after a failure.
 */
export function CaptchaChallengeProvider({
  children,
  scriptStatus,
  onRequestScript,
  onRetryScript,
}: CaptchaChallengeProviderProps) {
  const router = useRouter()
  const { t, currentLang } = useLocales()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))
  const [view, setView] = useState<ChallengeView | null>(null)
  const [container, setContainer] = useState<HTMLDivElement | null>(
    null,
  )
  const activeRef = useRef<PendingSolve | null>(null)
  const generationRef = useRef(0)
  const mountedRef = useRef(false)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const previousChromeRef = useRef(false)
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const chromeVisibleRef = useRef(false)
  const language = currentLang.value

  const settle = useCallback(
    (pending: PendingSolve, result: CaptchaSolveResult) => {
      if (activeRef.current !== pending || pending.settled) return

      pending.settled = true
      stopWatchdog(pending)
      removeCurrentWidget(pending)
      pending.generation = undefined
      activeRef.current = null
      if (mountedRef.current) setView(null)
      pending.resolve(result)
    },
    [],
  )

  const showError = useCallback(
    (pending: PendingSolve, generation: number, code: string) => {
      if (
        !isCurrentGeneration(pending, generation, activeRef.current)
      ) {
        return
      }

      stopWatchdog(pending)
      pending.showError = true
      try {
        pending.onWidgetError(code)
      } catch {
        // Widget diagnostics must not prevent the user from recovering.
      }
      removeCurrentWidget(pending, generation)
      blurActiveElement()
      setView((current) =>
        current?.generation === generation
          ? { ...current, showError: true, interactionVisible: true }
          : current,
      )
    },
    [],
  )

  const startWatchdog = useCallback(
    (pending: PendingSolve, generation: number) => {
      stopWatchdog(pending)
      if (
        !isCurrentGeneration(pending, generation, activeRef.current)
      ) {
        return
      }

      pending.watchdog = window.setTimeout(() => {
        pending.watchdog = undefined
        if (
          isCurrentGeneration(pending, generation, activeRef.current)
        ) {
          showError(pending, generation, CAPTCHA_WATCHDOG_ERROR_CODE)
        }
      }, CAPTCHA_SILENT_TIMEOUT_MS)
    },
    [showError],
  )

  const startFreshChallenge = useCallback(
    (
      pending: PendingSolve,
      options?: {
        waitForScriptLoad?: boolean
        scriptStatusAtRetry?: CaptchaScriptStatus
      },
    ) => {
      removeCurrentWidget(pending)
      stopWatchdog(pending)
      const generation = ++generationRef.current
      pending.generation = generation
      pending.stale =
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      pending.showError = false
      pending.waitingForScriptLoad = Boolean(
        options?.waitForScriptLoad,
      )
      pending.scriptStatusAtRetry = options?.scriptStatusAtRetry
      setView({
        challenge: pending.challenge,
        generation,
        interactionVisible: false,
        showError: false,
      })
      if (!pending.stale) startWatchdog(pending, generation)
      return generation
    },
    [startWatchdog],
  )

  const cancelActive = useCallback(() => {
    const pending = activeRef.current
    if (pending) settle(pending, { outcome: 'cancelled' })
  }, [settle])

  const solveCaptcha = useCallback<CaptchaSolver>(
    (
      challenge,
      { onWidgetError, action, cData, afterRejection = false },
    ) => {
      if (typeof window === 'undefined') {
        return Promise.resolve({
          outcome: 'failed',
          code: CAPTCHA_FAILURE_CODES.presentationUnavailable,
        })
      }
      if (challenge.mode !== 'managed') {
        return Promise.resolve({
          outcome: 'unsupported_mode',
          mode: challenge.mode,
        })
      }
      if (
        typeof challenge.siteKey !== 'string' ||
        challenge.siteKey.trim().length === 0
      ) {
        return Promise.resolve({
          outcome: 'failed',
          code: CAPTCHA_FAILURE_CODES.missingSiteKey,
        })
      }

      if (activeRef.current) {
        settle(activeRef.current, { outcome: 'cancelled' })
      }

      return new Promise<CaptchaSolveResult>((resolve) => {
        const generation = ++generationRef.current
        const pending: PendingSolve = {
          challenge,
          action,
          cData,
          generation,
          onWidgetError,
          resolve,
          settled: false,
          stale: document.visibilityState === 'hidden',
          showError: afterRejection,
          waitingForScriptLoad: false,
        }
        activeRef.current = pending
        setView({
          challenge,
          generation,
          interactionVisible: afterRejection,
          showError: afterRejection,
        })
        if (afterRejection) {
          blurActiveElement()
        } else if (!pending.stale) {
          startWatchdog(pending, generation)
        }

        try {
          onRequestScript?.()
        } catch {
          showError(pending, generation, CAPTCHA_SCRIPT_ERROR_CODE)
        }
      })
    },
    [onRequestScript, settle, showError, startWatchdog],
  )

  const retry = useCallback(() => {
    const pending = activeRef.current
    if (!pending || pending.settled || !pending.showError) return

    const shouldRetryScript =
      Boolean(onRetryScript) &&
      (scriptStatus === 'failed' || !getTurnstile())
    const generation = startFreshChallenge(pending, {
      waitForScriptLoad: shouldRetryScript,
      scriptStatusAtRetry: shouldRetryScript
        ? scriptStatus
        : undefined,
    })

    if (shouldRetryScript) {
      try {
        onRetryScript?.()
      } catch {
        showError(pending, generation, CAPTCHA_SCRIPT_ERROR_CODE)
      }
    }
  }, [onRetryScript, scriptStatus, showError, startFreshChallenge])

  useEffect(() => {
    mountedRef.current = true
    const handleRouteChange = () => cancelActive()
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        activeRef.current &&
        chromeVisibleRef.current
      ) {
        event.preventDefault()
        event.stopPropagation()
        cancelActive()
      }
    }
    const handleFocusIn = (event: FocusEvent) => {
      if (
        chromeVisibleRef.current &&
        event.target instanceof Node &&
        overlayRef.current?.contains(event.target)
      ) {
        event.stopPropagation()
      }
    }
    const handleVisibilityChange = () => {
      const pending = activeRef.current
      if (!pending || pending.settled) return

      if (document.visibilityState === 'hidden') {
        stopWatchdog(pending)
        pending.stale = true
        return
      }

      if (pending.stale && !pending.showError) {
        startFreshChallenge(pending)
      }
    }

    router.events.on('routeChangeStart', handleRouteChange)
    // Capture phase on window runs before MUI dialogs' FocusTrap/Escape
    // handlers on document, so a dialog under the challenge cannot swallow
    // Escape or pull focus out of it.
    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('focusin', handleFocusIn, true)
    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange,
    )

    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('focusin', handleFocusIn, true)
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange,
      )
      mountedRef.current = false
      cancelActive()
    }
  }, [cancelActive, router.events, startFreshChallenge])

  const viewGeneration = view?.generation
  const viewShowError = view?.showError

  useEffect(() => {
    if (viewGeneration === undefined || viewShowError || !container)
      return

    const pending = activeRef.current
    const generation = viewGeneration
    if (
      !pending ||
      !isCurrentGeneration(pending, generation, activeRef.current)
    ) {
      return
    }

    if (pending.waitingForScriptLoad) {
      if (scriptStatus === pending.scriptStatusAtRetry) return

      pending.waitingForScriptLoad = false
      pending.scriptStatusAtRetry = undefined
      if (scriptStatus === 'loading') return
    }

    if (scriptStatus === 'loading') return
    if (scriptStatus === 'failed') {
      showError(pending, generation, CAPTCHA_SCRIPT_ERROR_CODE)
      return
    }

    const turnstile = getTurnstile()
    if (!turnstile) {
      showError(pending, generation, CAPTCHA_SCRIPT_ERROR_CODE)
      return
    }

    const callbackToken = Symbol()
    pending.callbackToken = callbackToken
    const isCurrent = () =>
      isCurrentGeneration(pending, generation, activeRef.current) &&
      pending.callbackToken === callbackToken
    try {
      const widgetId = turnstile.render(container, {
        sitekey: pending.challenge.siteKey,
        language,
        theme: 'light',
        appearance: 'interaction-only',
        retry: 'never',
        'refresh-expired': 'manual',
        'refresh-timeout': 'manual',
        'response-field': false,
        size: 'flexible',
        ...(pending.action !== undefined
          ? { action: pending.action }
          : {}),
        ...(pending.cData !== undefined
          ? { cData: pending.cData }
          : {}),
        callback: (token) => {
          if (!isCurrent()) return
          if (typeof token !== 'string' || token.length === 0) {
            showError(pending, generation, 'empty_token')
            return
          }
          settle(pending, { outcome: 'solved', proof: { token } })
        },
        'expired-callback': () => {
          if (isCurrent()) startFreshChallenge(pending)
        },
        'timeout-callback': () => {
          if (isCurrent()) {
            showError(pending, generation, CAPTCHA_TIMEOUT_ERROR_CODE)
          }
        },
        'error-callback': (errorCode) => {
          if (isCurrent()) {
            showError(
              pending,
              generation,
              String(errorCode || 'unknown'),
            )
          }
          return true
        },
        'unsupported-callback': () => {
          if (isCurrent()) {
            showError(pending, generation, 'unsupported_browser')
          }
        },
        'before-interactive-callback': () => {
          if (!isCurrent()) return
          stopWatchdog(pending)
          blurActiveElement()
          setView((current) =>
            current?.generation === generation
              ? { ...current, interactionVisible: true }
              : current,
          )
        },
        'after-interactive-callback': () => {
          if (!isCurrent() || pending.showError) return
          startWatchdog(pending, generation)
          setView((current) =>
            current?.generation === generation
              ? { ...current, interactionVisible: false }
              : current,
          )
        },
      })

      const widget: WidgetInstance = {
        api: turnstile,
        generation,
        id: widgetId,
        removed: false,
      }
      if (isCurrent()) {
        pending.widget = widget
      } else {
        widget.removed = true
        turnstile.remove(widget.id)
      }
    } catch {
      if (isCurrent()) showError(pending, generation, 'render_failed')
    }

    return () => {
      if (pending.callbackToken === callbackToken) {
        pending.callbackToken = undefined
      }
      removeCurrentWidget(pending, generation)
    }
  }, [
    container,
    isDesktop,
    language,
    scriptStatus,
    settle,
    showError,
    startFreshChallenge,
    startWatchdog,
    viewGeneration,
    viewShowError,
  ])

  const showChrome = Boolean(
    view && (view.interactionVisible || view.showError),
  )
  chromeVisibleRef.current = showChrome

  useEffect(() => {
    if (showChrome && (!previousChromeRef.current || viewShowError)) {
      blurActiveElement()
      closeButtonRef.current?.focus()
    }
    previousChromeRef.current = showChrome
  }, [showChrome, viewShowError])

  const contextValue = useMemo(
    () => ({ solveCaptcha, cancelCaptcha: cancelActive }),
    [cancelActive, solveCaptcha],
  )

  return (
    <CaptchaChallengeContext.Provider value={contextValue}>
      {children}
      {view && (
        <Portal>
          <Box
            id="c-auth-captcha-overlay"
            ref={overlayRef}
            aria-hidden={showChrome ? undefined : true}
            sx={{
              position: 'fixed',
              inset: 0,
              // Above every MUI modal: a dialog opened mid-challenge must not
              // cover it.
              zIndex: theme.zIndex.modal + 1,
              display: 'flex',
              alignItems: isDesktop ? 'center' : 'flex-end',
              justifyContent: 'center',
              pointerEvents: showChrome ? 'auto' : 'none',
            }}
          >
            {showChrome && (
              <Box
                id="b-auth-captcha-backdrop"
                onClick={cancelActive}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: CAPTCHA_BACKDROP_COLOR,
                }}
              />
            )}
            <Box
              id="c-auth-captcha-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby={
                view.showError
                  ? 'c-auth-captcha-error-title'
                  : 'c-auth-captcha-title'
              }
              onClick={(event) => event.stopPropagation()}
              sx={{
                position: 'relative',
                zIndex: 1,
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: isDesktop
                  ? CAPTCHA_DIALOG_MAX_WIDTH
                  : 'none',
                p: showChrome && isDesktop ? 3 : 0,
                ...(showChrome && !isDesktop
                  ? {
                      // The widget cannot shrink below 300px, so on very
                      // narrow screens the side padding yields to it.
                      py: 3,
                      px: `min(16px, max(0px, calc((100% - ${CAPTCHA_WIDGET_MIN_WIDTH}px) / 2)))`,
                      pb: 'calc(24px + env(safe-area-inset-bottom))',
                    }
                  : {}),
                backgroundColor: showChrome
                  ? theme.palette.background.paper
                  : 'transparent',
                borderRadius: showChrome
                  ? isDesktop
                    ? '8px'
                    : '8px 8px 0 0'
                  : 0,
              }}
            >
              {view.showError ? (
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{ position: 'relative', textAlign: 'center' }}
                  >
                    <StateImage
                      type="system-error"
                      sx={{
                        display: 'block',
                        mx: 'auto',
                        maxWidth: '240px',
                      }}
                    />
                    <IconButton
                      id="b-auth-captcha-close"
                      aria-label={t('key_close')}
                      onClick={cancelActive}
                      ref={closeButtonRef}
                      sx={{ position: 'absolute', top: 0, right: 0 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Typography
                    id="c-auth-captcha-error-title"
                    variant="h5"
                    align="center"
                    sx={{ mt: 3 }}
                  >
                    {t('key_captcha_verification_failed_title')}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ mt: 1, color: 'text.secondary' }}
                  >
                    {t('key_captcha_verification_failed_description')}
                  </Typography>
                  <Button
                    id="b-auth-captcha-retry"
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={retry}
                    sx={{ mt: 3 }}
                  >
                    {t('key_try_again')}
                  </Button>
                </Box>
              ) : (
                showChrome && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography
                        id="c-auth-captcha-title"
                        variant="h5"
                        sx={{ color: 'text.primary' }}
                      >
                        {t('key_captcha_verification_title')}
                      </Typography>
                      <IconButton
                        id="b-auth-captcha-close"
                        aria-label={t('key_close')}
                        onClick={cancelActive}
                        ref={closeButtonRef}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ height: 24 }} />
                  </>
                )
              )}
              <Box
                id="c-auth-captcha-widget"
                ref={setContainer}
                sx={{
                  // Block layout lets `size: 'flexible'` stretch to the panel
                  // width.
                  display: 'block',
                  '& > div, & iframe': { width: '100% !important' },
                  width: '100%',
                  minWidth: CAPTCHA_WIDGET_MIN_WIDTH,
                  minHeight: view.showError
                    ? 0
                    : CAPTCHA_CONTAINER_MIN_HEIGHT,
                }}
              />
            </Box>
          </Box>
        </Portal>
      )}
    </CaptchaChallengeContext.Provider>
  )
}

export function useCaptchaChallenge(): CaptchaChallengeContextValue {
  const context = useContext(CaptchaChallengeContext)
  if (!context) {
    throw new Error(
      'useCaptchaChallenge must be used within CaptchaChallengeProvider',
    )
  }

  return context
}
